import { useCallback, useEffect, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useInView } from 'react-intersection-observer'
import { ListingSectionObject } from '../transports/common/listing-section-transport'
import { trackSrlListingViewV2, trackSrlListingClickV2 } from '../utils/track-utils'
import { CookieManagerService } from '../services'
import { useSearchRegionNode } from '../context/search-region-node'
import { getSearchRequestId } from '../redux/filters/selectors'
import AnalyticsService from '../services/analytics-service'
import { pdpPageActions } from '../services/analytics-service/actions'
import { getCategory } from '../utils/category-utils'

const _srlTrackingObserverOptions = {
    root: null,
    threshold: 0.5,
    triggerOnce: true,
}

/**
 * Hook for SRL list view tracking via v2 API.
 * - Tracks view events (21 + 13 if top) when listing enters viewport
 * - Tracks click events (22 + 14 if top) when listing is clicked
 * - Also sets existing cookies for backward compatibility with cookie-based tracking
 *
 * Uses SearchRegionNodeContext to get search filter data (adults, children, arrival, departure)
 * without prop drilling.
 */
export const useSrlTracking = (listing: ListingSectionObject | null, index: number) => {
    const [intersectionRef, inView] = useInView(_srlTrackingObserverOptions)
    const hasTrackedView = useRef(false)
    const searchRegionNode = useSearchRegionNode()
    const searchRequestId = useSelector(getSearchRequestId)

    const filterData = useMemo(
        () => ({
            adult: searchRegionNode?.adults ?? 2,
            children: searchRegionNode?.children ?? 0,
            arrival_date: searchRegionNode?.arrival ?? null,
            departure_date: searchRegionNode?.departure ?? null,
            search_request_id: searchRequestId,
        }),
        [searchRegionNode, searchRequestId],
    )

    useEffect(() => {
        if (!listing) {
            return
        }
        if (inView && !hasTrackedView.current) {
            hasTrackedView.current = true
            trackSrlListingViewV2(listing, index, filterData)
        }
    }, [inView, listing, index, filterData])

    const handleSrlClick = useCallback(() => {
        if (!listing) {
            return
        }
        trackSrlListingClickV2(listing, index, filterData)

        AnalyticsService.trackEvent({
            event: pdpPageActions.SELECT_ITEM,
            ecommerce: {
                items: [
                    {
                        item_id: listing.id,
                        item_name: listing.title,
                        affiliation: listing.providerId,
                        item_category: getCategory(!!listing.isComboListing),
                        price: listing.price?.notFormattedPrice,
                        currency: listing.price?.currency,
                        quantity: 1,
                    },
                ],
            },
            arrival: filterData.arrival_date ?? undefined,
            departure: filterData.departure_date ?? undefined,
            persons: filterData.adult + filterData.children || undefined,
        })

        // Set cookies for backward compatibility (existing cookie-based click tracking)
        CookieManagerService.setCookie('srlObjectClicked_' + listing.id, globalThis.location.href, {
            expires: 7,
        })
        CookieManagerService.setCookie('srlObjectClicked_' + listing.id + '_componentName', 'unknown', {
            expires: 7,
        })
    }, [listing, index, filterData])

    return { intersectionRef, handleSrlClick }
}
